/**
 * WebSocket Hook for Real-time Dashboard Updates
 * AS9100D Clause 9.1: Monitoring, Measurement, Analysis
 * 
 * Provides real-time push notifications for inventory events:
 * - GRN arrivals and completions
 * - Issue completions
 * - Quarantine alerts (including counterfeit suspects)
 * - Cycle count updates
 * - QC status changes
 * 
 * @version 1.0.0
 * @date January 2026
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';

// Event types matching backend EventType enum
export const EventTypes = {
  // GRN Events
  GRN_CREATED: 'grn.created',
  GRN_RECEIVED: 'grn.received',
  GRN_COMPLETED: 'grn.completed',
  
  // Issue Events
  ISSUE_CREATED: 'issue.created',
  ISSUE_COMPLETED: 'issue.completed',
  
  // Transfer Events
  TRANSFER_CREATED: 'transfer.created',
  TRANSFER_EXECUTED: 'transfer.executed',
  
  // QC Events
  INSPECTION_CREATED: 'inspection.created',
  INSPECTION_COMPLETED: 'inspection.completed',
  QC_HOLD_ADDED: 'qc_hold.added',
  QC_RELEASED: 'qc.released',
  QC_REJECTED: 'qc.rejected',
  
  // Quarantine Events
  QUARANTINE_CREATED: 'quarantine.created',
  QUARANTINE_RELEASED: 'quarantine.released',
  COUNTERFEIT_ALERT: 'quarantine.counterfeit_alert',
  DISPOSITION_REQUESTED: 'disposition.requested',
  DISPOSITION_APPROVED: 'disposition.approved',
  SCRAP_CONFIRMED: 'scrap.confirmed',
  
  // Cycle Count Events
  COUNT_SCHEDULED: 'count.scheduled',
  COUNT_STARTED: 'count.started',
  COUNT_COMPLETED: 'count.completed',
  VARIANCE_DETECTED: 'count.variance_detected',
  
  // Stock Events
  STOCK_LOW: 'stock.low',
  STOCK_EXPIRING: 'stock.expiring',
  
  // Part Master Events (INT-005)
  PART_UPDATED: 'part.updated',
  PART_DELETED: 'part.deleted',
  PART_CREATED: 'part.created',
  PART_RESTORED: 'part.restored',
  
  // System Events
  DASHBOARD_REFRESH: 'dashboard.refresh',
  CONNECTION_ACK: 'connection.ack',
  HEARTBEAT: 'heartbeat'
};

// Severity levels for toast notifications
const severityConfig = {
  info: {
    toastType: 'info',
    icon: '📋'
  },
  warning: {
    toastType: 'warning',
    icon: '⚠️'
  },
  error: {
    toastType: 'error',
    icon: '❌'
  },
  critical: {
    toastType: 'error',
    icon: '🚨'
  }
};

/**
 * Custom hook for WebSocket connection to inventory dashboard
 * 
 * @param {Object} options - Hook options
 * @param {boolean} options.enabled - Whether to enable WebSocket connection
 * @param {function} options.onRefresh - Callback when dashboard should refresh
 * @param {function} options.onEvent - Callback for all events
 * @param {boolean} options.showToasts - Whether to show toast notifications
 * @returns {Object} WebSocket state and methods
 */
export const useInventoryWebSocket = ({ 
  enabled = true, 
  onRefresh = null, 
  onEvent = null,
  showToasts = true 
} = {}) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  
  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const [eventCount, setEventCount] = useState(0);
  const [connectionError, setConnectionError] = useState(null);
  
  // Build WebSocket URL
  const getWebSocketUrl = useCallback(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      console.warn('No auth token for WebSocket connection');
      return null;
    }
    
    // Get base URL from environment
    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    
    // Convert http(s) to ws(s)
    const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = baseUrl.replace(/^https?:\/\//, '');
    
    return `${wsProtocol}://${wsHost}/api/v1/inventory/v2/ws/dashboard?token=${token}`;
  }, []);
  
  // Handle incoming WebSocket message
  const handleMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);
      
      // Update state
      setLastEvent(data);
      setEventCount(prev => prev + 1);
      
      // Call event callback
      if (onEvent) {
        onEvent(data);
      }
      
      // Handle specific event types
      switch (data.event_type) {
        case EventTypes.CONNECTION_ACK:
          console.log('WebSocket: Connection acknowledged', data.data);
          break;
          
        case EventTypes.HEARTBEAT:
          // Silent handling
          break;
          
        case EventTypes.DASHBOARD_REFRESH:
          if (onRefresh) {
            onRefresh();
          }
          if (showToasts) {
            toast.info('Dashboard updating...', { duration: 2000 });
          }
          break;
          
        case EventTypes.COUNTERFEIT_ALERT:
          // Critical alert - always show
          toast.error(data.message || 'Counterfeit suspect detected!', {
            duration: 10000,
            description: data.title,
            action: data.action_url ? {
              label: 'View',
              onClick: () => window.location.href = data.action_url
            } : undefined
          });
          if (onRefresh) onRefresh();
          break;
          
        case EventTypes.QUARANTINE_CREATED:
          if (showToasts) {
            toast.warning(data.message, {
              duration: 5000,
              description: 'New quarantine item'
            });
          }
          if (onRefresh) onRefresh();
          break;
          
        case EventTypes.GRN_CREATED:
        case EventTypes.GRN_RECEIVED:
        case EventTypes.GRN_COMPLETED:
          if (showToasts) {
            const config = severityConfig[data.severity] || severityConfig.info;
            toast(data.message, {
              duration: 4000,
              description: data.title,
              icon: config.icon
            });
          }
          if (onRefresh) onRefresh();
          break;
          
        case EventTypes.ISSUE_COMPLETED:
          if (showToasts) {
            toast.success(data.message, { duration: 3000 });
          }
          if (onRefresh) onRefresh();
          break;
          
        case EventTypes.COUNT_COMPLETED:
          if (showToasts) {
            toast.success(data.message, { duration: 3000 });
          }
          if (onRefresh) onRefresh();
          break;
          
        case EventTypes.VARIANCE_DETECTED:
          if (showToasts) {
            const severity = data.severity || 'warning';
            if (severity === 'critical') {
              toast.error(data.message, { duration: 6000 });
            } else {
              toast.warning(data.message, { duration: 5000 });
            }
          }
          if (onRefresh) onRefresh();
          break;
          
        // INT-005: Part Master Event Consumers
        case EventTypes.PART_UPDATED:
          if (showToasts) {
            toast.info(data.message, {
              duration: 4000,
              description: 'Part data has changed - refreshing inventory',
              icon: '🔄'
            });
          }
          // Dispatch custom event for components to listen to
          window.dispatchEvent(new CustomEvent('part-master-update', { 
            detail: {
              hpn_compact: data.data?.hpn_compact,
              change_type: data.data?.change_type,
              changed_fields: data.data?.changed_fields
            }
          }));
          if (onRefresh) onRefresh();
          break;
          
        case EventTypes.PART_DELETED:
          if (showToasts) {
            toast.warning(data.message, {
              duration: 5000,
              description: 'Part has been removed - updating inventory',
              icon: '⚠️'
            });
          }
          // Dispatch custom event for components to handle deleted parts
          window.dispatchEvent(new CustomEvent('part-master-delete', { 
            detail: {
              hpn_compact: data.data?.hpn_compact,
              reason: data.data?.reason
            }
          }));
          if (onRefresh) onRefresh();
          break;
          
        case EventTypes.PART_CREATED:
          if (showToasts) {
            toast.success(data.message, {
              duration: 4000,
              description: 'New part available in catalog',
              icon: '✨'
            });
          }
          window.dispatchEvent(new CustomEvent('part-master-create', { 
            detail: {
              hpn_compact: data.data?.hpn_compact,
              description: data.data?.description
            }
          }));
          if (onRefresh) onRefresh();
          break;
          
        case EventTypes.PART_RESTORED:
          if (showToasts) {
            toast.info(data.message, {
              duration: 4000,
              description: 'Part restored to catalog',
              icon: '↩️'
            });
          }
          window.dispatchEvent(new CustomEvent('part-master-restore', { 
            detail: {
              hpn_compact: data.data?.hpn_compact
            }
          }));
          if (onRefresh) onRefresh();
          break;
          
        default:
          // Generic event handling
          if (showToasts && data.severity && data.message) {
            const config = severityConfig[data.severity] || severityConfig.info;
            toast(data.message, {
              duration: 3000,
              icon: config.icon
            });
          }
          // Refresh dashboard for any data-changing event
          if (data.event_type && !data.event_type.startsWith('heartbeat') && 
              !data.event_type.startsWith('connection')) {
            if (onRefresh) onRefresh();
          }
      }
      
    } catch (err) {
      console.error('WebSocket: Error parsing message', err);
    }
  }, [onEvent, onRefresh, showToasts]);
  
  // Use ref for reconnection to avoid circular dependency
  const shouldReconnectRef = useRef(true);
  
  // Connect to WebSocket
  const connect = useCallback(() => {
    const url = getWebSocketUrl();
    if (!url) {
      setConnectionError('No authentication token');
      return;
    }
    
    try {
      // Close existing connection
      if (wsRef.current) {
        wsRef.current.close();
      }
      
      console.log('WebSocket: Connecting...');
      wsRef.current = new WebSocket(url);
      
      wsRef.current.onopen = () => {
        console.log('WebSocket: Connected');
        setIsConnected(true);
        setConnectionError(null);
        
        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000); // Ping every 30 seconds
      };
      
      wsRef.current.onmessage = handleMessage;
      
      wsRef.current.onclose = (event) => {
        console.log('WebSocket: Disconnected', event.code, event.reason);
        setIsConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        // Attempt reconnection unless intentionally closed
        if (event.code !== 1000 && event.code !== 4001 && shouldReconnectRef.current) {
          console.log('WebSocket: Scheduling reconnection...');
          // Use window.location.reload or manual reconnect trigger instead
          reconnectTimeoutRef.current = setTimeout(() => {
            if (shouldReconnectRef.current) {
              // Trigger page-level reconnection logic
              window.dispatchEvent(new CustomEvent('websocket-reconnect'));
            }
          }, 5000);
        } else if (event.code === 4001) {
          setConnectionError('Authentication failed');
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket: Error', error);
        setConnectionError('Connection error');
      };
      
    } catch (err) {
      console.error('WebSocket: Connection failed', err);
      setConnectionError(err.message);
    }
  }, [getWebSocketUrl, handleMessage]);
  
  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    console.log('WebSocket: Disconnecting...');
    shouldReconnectRef.current = false;
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnect');
      wsRef.current = null;
    }
    
    setIsConnected(false);
  }, []);
  
  // Effect to manage connection lifecycle
  useEffect(() => {
    // Handle reconnection events
    const handleReconnect = () => {
      if (enabled && shouldReconnectRef.current) {
        const url = getWebSocketUrl();
        if (url) {
          // Close existing and reconnect
          if (wsRef.current) {
            wsRef.current.close();
          }
          // Trigger connect through the normal flow
          shouldReconnectRef.current = true;
        }
      }
    };
    
    window.addEventListener('websocket-reconnect', handleReconnect);
    
    return () => {
      window.removeEventListener('websocket-reconnect', handleReconnect);
    };
  }, [enabled, getWebSocketUrl]);
  
  // Separate effect for initial connection (runs once)
  useEffect(() => {
    let mounted = true;
    
    const initConnection = () => {
      if (mounted && enabled) {
        shouldReconnectRef.current = true;
        connect();
      }
    };
    
    // Small delay to ensure component is fully mounted
    const timer = setTimeout(initConnection, 100);
    
    return () => {
      mounted = false;
      clearTimeout(timer);
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
  
  // Reconnect on token change
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'access_token' && enabled) {
        console.log('WebSocket: Token changed, reconnecting...');
        disconnect();
        const timer = setTimeout(() => {
          shouldReconnectRef.current = true;
          connect();
        }, 1000);
        return () => clearTimeout(timer);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
  
  return {
    isConnected,
    lastEvent,
    eventCount,
    connectionError,
    connect,
    disconnect
  };
};

export default useInventoryWebSocket;
